import { Flex } from '../../common/Flex';
import { Col, notification, Radio, Row, Select, Space, Tabs } from 'antd';
import { SettingFilled } from '@ant-design/icons';
import React, { PureComponent } from 'react';
import {
    CapacityModel,
    CapacityValue,
    CountType,
    FlightId,
    MonitoredLoadValue,
    MonitoredOccupancyLoadValues,
    TimePeriod,
    TrafficCountsDto,
    TrafficType,
} from 'app/model/CORE';
import { EntryLoadBar } from '../../dashboard/network/flows/svg/EntryLoadBar';
import { OccupancyLoadBar } from '../../dashboard/network/flows/svg/OccupancyLoadBar';
import { CountsGraph } from '../tv/graph/CountsGraph';
import TrafficCountUtils from '../../../common/util/TrafficCountUtils';
import MonitoredLoadUtils from '../../../common/util/MonitoredLoadUtils';
import AppContext from '../../../common/context/AppContext';
import TimePeriodUtils from '../../../common/util/TimePeriodUtils';
import memoizeOne from 'memoize-one';
import { RegulationPeriods } from 'app/model/client/CORE';
import CapacityUtils from '../../../common/util/CapacityUtils';
import Utils from '../../../common/util/Utils';
import { ConvectionSource, TvMeteoAlert } from 'app/model/SOL49';
import { MeteoLoadBar } from 'app/components/dashboard/network/flows/svg/MeteoLoadBar';
import { ServiceFactory } from 'app/service/ServiceFactory';

interface TvCountsProps {
    // Entry
    entryCounts: TrafficCountsDto;
    entryComparisonCounts?: TrafficCountsDto;
    entryTrafficType: TrafficType.LOAD;
    entryInterval: number;
    entryStep: number;

    // Occupancy
    occCounts: TrafficCountsDto;
    occComparisonCounts?: TrafficCountsDto;
    occTrafficType: TrafficType.LOAD;
    otmvDuration: number;
    loadValues?: Array<MonitoredLoadValue>;
    occupancyLoadValues?: Array<MonitoredOccupancyLoadValues>;

    hideSettings?: boolean;
    currentTime: Date;
    tvId: string;
    tvSet: string;
    regulationPeriods?: Array<RegulationPeriods>;
    occupancyPeriods?: Map<FlightId, TimePeriod>;
    onUpdate?(): void;
    selectedPeriod?: TimePeriod;
    selectedPeriodType?: CountType;
    onSelectedPeriodChange?: (
        countType: CountType,
        selectedPeriod: TimePeriod,
        trafficWindow: TimePeriod,
        hourlyEntryCapacity?: Array<CapacityValue>,
    ) => void;
    meteoAlerts: Array<TvMeteoAlert>;
}

interface TvCountsState {
    display: 'entry' | 'occupancy';
    meteoAlerts?: Array<TvMeteoAlert>;
    predictedCapacity?: CapacityValue[];

    // Internal state
    timeOver?: Date;
}

export class TvCounts extends PureComponent<TvCountsProps, TvCountsState> {
    constructor(props: TvCountsProps) {
        super(props);

        this.state = {
            display: 'entry',
            meteoAlerts: this.props.meteoAlerts,
        };
    }

    componentDidMount() {
        if (this.props.tvId) {
            if (!this.state.meteoAlerts) {
                ServiceFactory.sol49Service()
                    .getTvMeteoAlerts(this.props.tvId, AppContext.getConvectionSource())
                    .then(reply => {
                        if (reply.error) {
                            console.error('Error fetching meteo alerts for tv:', this.props.tvId, ' => ', reply.error);
                        } else {
                            this.setState(
                                {
                                    meteoAlerts: reply.data,
                                },
                                () => this.getPredictedCapacity(),
                            );
                        }
                    })
                    .catch(reason =>
                        notification.error({ message: 'Error', description: reason.message, duration: 0 }),
                    );
            } else {
                this.getPredictedCapacity();
            }
        }
    }

    getPredictedCapacity = () => {
        if (Utils.isEmpty(this.state.meteoAlerts)) return;

        // ISOBAR service is DWD specific (needs DWD convectionFraction and Likeliness info)
        if (
            AppContext.getConvectionSource() === ConvectionSource.DWD ||
            AppContext.getConvectionSource() === ConvectionSource.DWD_ICONV
        ) {
            const obj = {};
            obj[this.props.tvId] = this.state.meteoAlerts;
            ServiceFactory.sol49Service()
                .getPredictedCapacity(AppContext.getConvectionSource(), obj)
                .then(reply => {
                    const error =
                        reply.error ||
                        (Utils.isNotEmpty(reply.data.errors) &&
                            reply.data.errors.find(
                                e => e.message.includes('No inference') || e.details?.includes('No inference'),
                            ));
                    if (error) {
                        console.error('Error fetching predicted capacity for tv:', this.props.tvId, ' => ', error);
                        notification.error({ message: error.message, description: error?.details });
                    } else {
                        this.setState({ predictedCapacity: reply.data.predictions[0].predictedCapacity });
                    }
                })
                .catch(reason => notification.error({ message: 'Error', description: reason.message, duration: 0 }));
        }
    };

    getCountingPeriod = (countType: CountType, selectedPeriod: TimePeriod) => {
        if (TimePeriodUtils.isValid(selectedPeriod)) {
            if (countType === CountType.ENTRY) {
                return TrafficCountUtils.countingPeriodFor2(
                    selectedPeriod,
                    this.props.entryInterval,
                    this.props.entryStep,
                );
            } else {
                return TrafficCountUtils.countingPeriodFor2(selectedPeriod, 1, this.props.otmvDuration); // Default interval supported only
            }
        }
    };

    handleTimeOverChange = (timeOver: Date) => {
        this.setState({ timeOver: timeOver });
    };

    handleSelectedPeriodChange = (countType: CountType) => (selectedPeriod: TimePeriod) => {
        const trafficWindow = this.getCountingPeriod(countType, selectedPeriod);
        this.props.onSelectedPeriodChange?.(
            countType,
            selectedPeriod,
            trafficWindow,
            this.props.entryCounts?.hourlyEntryCapacity,
        );
    };

    getActivePeriods = memoizeOne((monitoredLoadValues: Array<MonitoredLoadValue>) => {
        return monitoredLoadValues && MonitoredLoadUtils.getActivePeriods(monitoredLoadValues);
    });

    renderEntryCounts = () => {
        const hourlyEntryCapacity =
            AppContext.getCapacityModel() === CapacityModel.WCM_NRP
                ? CapacityUtils.getWcmCapacity(
                      this.props.entryCounts?.hourlyEntryCapacity,
                      this.props.entryCounts?.timeRange,
                  )
                : this.props.entryCounts?.hourlyEntryCapacity;

        return (
            <CountsGraph
                currentTime={this.props.currentTime}
                counts={
                    this.props.entryCounts &&
                    TrafficCountUtils.getCountsFor(this.props.entryCounts, this.props.entryTrafficType)
                }
                comparisonCounts={
                    this.props.entryComparisonCounts &&
                    TrafficCountUtils.getCountsFor(this.props.entryComparisonCounts, this.props.entryTrafficType)
                }
                activePeriods={this.getActivePeriods(this.props.loadValues)}
                timeRange={this.props.entryCounts?.timeRange}
                hourlyEntryCapacity={hourlyEntryCapacity}
                predictedCapacity={this.state.predictedCapacity}
                regulationPeriods={this.props.regulationPeriods}
                highlightedFlightsType={CountType.ENTRY}
                highlightedFlightsOccupancy={this.props.occupancyPeriods}
                timeOver={this.state.timeOver}
                onTimeOverChange={this.handleTimeOverChange}
                selectedPeriod={this.props.selectedPeriodType === CountType.ENTRY && this.props.selectedPeriod}
                onSelectedPeriodChange={this.handleSelectedPeriodChange(CountType.ENTRY)}
            />
        );
    };
    renderEntryLoad = () => {
        return (
            <Flex>
                <Flex
                    size="30px"
                    style={{
                        fontWeight: 'bold',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingRight: '5px',
                    }}>
                    E
                </Flex>
                <Flex direction="vertical">
                    <EntryLoadBar
                        loadValues={this.props.loadValues}
                        monitoredPeriod={this.props.entryCounts?.timeRange}
                        activePeriods={this.getActivePeriods(this.props.loadValues)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                    />
                    <MeteoLoadBar
                        loadValues={MonitoredLoadUtils.getTvMeteoAlertsLoadValues(
                            this.props.meteoAlerts,
                            this.props.entryCounts?.timeRange,
                        )}
                        monitoredPeriod={this.props.entryCounts?.timeRange}
                        barHeightEm={0.5}
                        hideLabel={true}
                    />
                </Flex>
            </Flex>
        );
    };
    renderEntry = () => {
        return (
            <Flex>
                <Flex direction="vertical" style={{ paddingTop: '1em' }}>
                    <Flex size="25em" className="counts">
                        {this.renderEntryCounts()}
                    </Flex>
                    <Flex size="1em">{this.renderEntryLoad()}</Flex>
                </Flex>
                {this.renderEntrySettings()}
            </Flex>
        );
    };

    renderEntrySettings = () => {
        if (this.props.hideSettings) return null;

        return (
            <Flex direction="vertical" size="10em" style={{ paddingLeft: '1em' }}>
                <div style={{ alignSelf: 'flex-end', paddingBottom: '0.5em' }}>
                    <SettingFilled style={{ fontSize: '2em' }} />
                </div>
                <Space direction="vertical">
                    <Select value="by_type" style={{ width: '100%' }}>
                        <Select.Option value="by_type">By type</Select.Option>
                        <Select.Option disabled={true} value="by_flow">
                            By flow
                        </Select.Option>
                    </Select>
                    <Select value={this.props.entryTrafficType} disabled={true} style={{ width: '100%' }}>
                        <Select.Option value={TrafficType.LOAD}>Load</Select.Option>
                        <Select.Option value={TrafficType.DEMAND}>Demand</Select.Option>
                        <Select.Option value={TrafficType.REGULATED_DEMAND}>Regulated demand</Select.Option>
                    </Select>
                    <Row gutter={[8, 8]}>
                        <Col span={24}>
                            Interval
                            <Select value={this.props.entryInterval} disabled={true} style={{ width: '100%' }}>
                                <Select.Option value={5}>5</Select.Option>
                                <Select.Option value={10}>10</Select.Option>
                                <Select.Option value={20}>20</Select.Option>
                                <Select.Option value={60}>60</Select.Option>
                            </Select>
                        </Col>
                        <Col span={24}>
                            Step
                            <Select value={this.props.entryStep} disabled={true} style={{ width: '100%' }}>
                                <Select.Option value={5}>5</Select.Option>
                                <Select.Option value={10}>10</Select.Option>
                                <Select.Option value={20}>20</Select.Option>
                                <Select.Option value={60}>60</Select.Option>
                            </Select>
                        </Col>
                    </Row>
                </Space>
            </Flex>
        );
    };
    renderOccupancyCounts = () => {
        return (
            <CountsGraph
                currentTime={this.props.currentTime}
                counts={
                    this.props.occCounts &&
                    TrafficCountUtils.getCountsFor(this.props.occCounts, this.props.occTrafficType)
                }
                comparisonCounts={
                    this.props.occComparisonCounts &&
                    TrafficCountUtils.getCountsFor(this.props.occComparisonCounts, this.props.occTrafficType)
                }
                activePeriods={this.getActivePeriods(this.props.loadValues)}
                timeRange={this.props.occCounts?.timeRange}
                otmvDefns={this.props.occCounts?.otmvDefns}
                regulationPeriods={this.props.regulationPeriods}
                highlightedFlightsType={CountType.OCCUPANCY}
                highlightedFlightsOccupancy={this.props.occupancyPeriods}
                timeOver={this.state.timeOver}
                onTimeOverChange={this.handleTimeOverChange}
                selectedPeriod={this.props.selectedPeriodType === CountType.OCCUPANCY && this.props.selectedPeriod}
                onSelectedPeriodChange={this.handleSelectedPeriodChange(CountType.OCCUPANCY)}
            />
        );
    };
    renderOccupancyLoad = () => {
        const occLoad =
            (this.props.occupancyLoadValues?.length > 0 && this.props.occupancyLoadValues[0].occupancyLoadValues) || [];
        return (
            <Flex>
                <Flex
                    size="30px"
                    style={{
                        fontWeight: 'bold',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingRight: '5px',
                    }}>
                    O
                </Flex>
                <Flex direction="vertical">
                    <OccupancyLoadBar
                        loadValues={occLoad}
                        monitoredPeriod={this.props.occCounts?.timeRange}
                        activePeriods={this.getActivePeriods(this.props.loadValues)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                    />
                    <MeteoLoadBar
                        loadValues={MonitoredLoadUtils.getTvMeteoAlertsLoadValues(
                            this.props.meteoAlerts,
                            this.props.occCounts?.timeRange,
                        )}
                        monitoredPeriod={this.props.occCounts?.timeRange}
                        barHeightEm={0.5}
                        hideLabel={true}
                    />
                </Flex>
            </Flex>
        );
    };
    /**
     * Occupancy is not displayed until data could be retrieved.
     * If TvId refers to a refloc != AS, then there won't be occupancy counts returned
     */
    renderOccupancy = () => {
        if (!this.props.occCounts) return undefined;
        return (
            <Flex>
                <Flex direction="vertical" style={{ paddingTop: '1em' }}>
                    <Flex size="25em" className="counts">
                        {this.renderOccupancyCounts()}
                    </Flex>
                    <Flex size="1em">{this.renderOccupancyLoad()}</Flex>
                </Flex>
                {this.renderOccupancySettings()}
            </Flex>
        );
    };

    renderOccupancySettings = () => {
        if (this.props.hideSettings) return null;

        return (
            <Flex direction="vertical" size="10em" style={{ paddingLeft: '1em' }}>
                <div style={{ alignSelf: 'flex-end', paddingBottom: '0.5em' }}>
                    <SettingFilled style={{ fontSize: '2em' }} />
                </div>
                <Space direction="vertical">
                    <Select value="by_type" style={{ width: '100%' }}>
                        <Select.Option value="by_type">by type</Select.Option>
                    </Select>
                    <Select value={this.props.occTrafficType} disabled={true} style={{ width: '100%' }}>
                        <Select.Option value={TrafficType.LOAD}>Load</Select.Option>
                        <Select.Option value={TrafficType.DEMAND}>Demand</Select.Option>
                        <Select.Option value={TrafficType.REGULATED_DEMAND}>Regulated demand</Select.Option>
                    </Select>
                    <Select value={this.props.otmvDuration} disabled={true} style={{ width: '100%' }}>
                        <Select.Option key="duration" value={this.props.otmvDuration}>
                            {this.props.otmvDuration} min
                        </Select.Option>
                    </Select>
                </Space>
            </Flex>
        );
    };

    render() {
        return (
            <Tabs
                activeKey={this.state.display}
                centered={true}
                animated={true}
                renderTabBar={() => (
                    <div style={{ paddingLeft: '3em' }}>
                        <Radio.Group
                            value={this.state.display}
                            onChange={value => this.setState({ display: value.target.value })}>
                            <Radio.Button disabled={!Utils.isExists(this.props.entryCounts)} value="entry">
                                Entry
                            </Radio.Button>
                            <Radio.Button disabled={!Utils.isExists(this.props.occCounts)} value="occupancy">
                                Occupancy
                            </Radio.Button>
                        </Radio.Group>
                    </div>
                )}
                style={{ width: '100%' }}
                items={[
                    {
                        label: 'entry',
                        key: 'entry',
                        children: this.renderEntry(),
                    },
                    {
                        label: 'occupancy',
                        key: 'occupancy',
                        children: this.renderOccupancy(),
                    },
                ]}
            />
        );
    }
}
